/**
 * PTW-AUD-860 (Task #3606) — Outbound PTW webhook subsystem.
 *
 * Two tenant-scoped tables back the outbound webhook delivery pipeline:
 *
 *   - `ptw_webhook_subscriptions` — a tenant-admin-managed registry of
 *     outbound endpoints. Each row carries an HMAC signing secret used to
 *     sign the request body (header `X-Nashid-Signature`). The signing secret
 *     is NEVER returned in any API response and is NEVER logged — it is
 *     revealed exactly once at creation time by the route layer.
 *   - `ptw_webhook_deliveries` — the durable delivery queue. The background
 *     worker drains pending/failed rows with exponential backoff, idempotency
 *     (`idempotency_key`), and a dead-letter terminal state after
 *     `max_attempts`.
 *
 * Precedent: shared/schema/public-surface-hardening.ts.
 */
import { sql } from "drizzle-orm";
import {
  pgTable,
  text,
  varchar,
  integer,
  boolean,
  timestamp,
  jsonb,
  unique,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { tenants } from "./common";

export const ptwWebhookSubscriptions = pgTable(
  "ptw_webhook_subscriptions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id),
    targetUrl: text("target_url").notNull(),
    /**
     * Raw HMAC-SHA256 signing secret. SECURITY: never serialise this column
     * into an API response and never write it to a log line. The route layer
     * reveals it exactly once on creation.
     */
    secretHash: text("secret_hash").notNull(),
    eventTypes: text("event_types").array().notNull(),
    active: boolean("active").notNull().default(true),
    description: text("description"),
    createdBy: text("created_by"),
    failureCount: integer("failure_count").notNull().default(0),
    disabledAt: timestamp("disabled_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    idxTenant: index("idx_ptw_webhook_subscriptions_tenant").on(t.tenantId),
    idxTenantActive: index("idx_ptw_webhook_subscriptions_tenant_active").on(
      t.tenantId,
      t.active,
    ),
  }),
);

export const ptwWebhookDeliveries = pgTable(
  "ptw_webhook_deliveries",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenants.id),
    subscriptionId: varchar("subscription_id")
      .notNull()
      .references(() => ptwWebhookSubscriptions.id),
    eventType: text("event_type").notNull(),
    permitId: text("permit_id"),
    payload: jsonb("payload")
      .$type<Record<string, unknown>>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    status: text("status").notNull().default("pending"),
    attempt: integer("attempt").notNull().default(0),
    maxAttempts: integer("max_attempts").notNull().default(6),
    nextAttemptAt: timestamp("next_attempt_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    lastError: text("last_error"),
    responseStatus: integer("response_status"),
    idempotencyKey: text("idempotency_key").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    uqIdempotency: unique("uq_ptw_webhook_deliveries_idempotency").on(
      t.idempotencyKey,
    ),
    idxTenant: index("idx_ptw_webhook_deliveries_tenant").on(t.tenantId),
    idxDrain: index("idx_ptw_webhook_deliveries_drain").on(
      t.status,
      t.nextAttemptAt,
    ),
    idxSubscription: index("idx_ptw_webhook_deliveries_subscription").on(
      t.subscriptionId,
    ),
  }),
);

export const insertPtwWebhookSubscriptionSchema = createInsertSchema(
  ptwWebhookSubscriptions,
).omit({
  id: true,
  failureCount: true,
  disabledAt: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPtwWebhookSubscription = z.infer<
  typeof insertPtwWebhookSubscriptionSchema
>;
export type PtwWebhookSubscription =
  typeof ptwWebhookSubscriptions.$inferSelect;

export const insertPtwWebhookDeliverySchema = createInsertSchema(
  ptwWebhookDeliveries,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPtwWebhookDelivery = z.infer<
  typeof insertPtwWebhookDeliverySchema
>;
export type PtwWebhookDelivery = typeof ptwWebhookDeliveries.$inferSelect;

/** Terminal + transient delivery states (see worker state machine). */
export const PTW_WEBHOOK_DELIVERY_STATUSES = [
  "pending",
  "delivering",
  "succeeded",
  "failed",
  "dead_letter",
] as const;
export type PtwWebhookDeliveryStatus =
  (typeof PTW_WEBHOOK_DELIVERY_STATUSES)[number];

/** Canonical PTW lifecycle event keys that may be subscribed to (or "*"). */
export const PTW_WEBHOOK_EVENT_TYPES = [
  "ptw.permit.created",
  "ptw.permit.submitted",
  "ptw.permit.issued",
  "ptw.permit.work_started",
  "ptw.permit.suspended",
  "ptw.permit.closed",
  "ptw.permit.cancelled",
] as const;
export type PtwWebhookEventType = (typeof PTW_WEBHOOK_EVENT_TYPES)[number];
